import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { BaseIntegration, IIntegrationAttributes } from '@feathr/blackbox';
import { noop, submitModelWithErrorHandling, toast } from '@feathr/components';
import { errorMessage } from '@feathr/hooks';

import type { ICrmMappingDraft } from './crmMappings.hooks';

/** A magpie-backed CRM integration whose tabs stage edits for one Save & Sync. */
export interface ICrmStagedIntegration<TStaged extends string = string>
  extends BaseIntegration<IIntegrationAttributes> {
  readonly changedStagedAttributes: TStaged[];
  getStagedPatch(): Partial<Record<TStaged, unknown>>;
  discardStagedChanges(): void;
}

export interface ICrmSave {
  /** Whether a staged attribute or mapping has unsaved changes. Other dirty attributes don't count. */
  isDirty: boolean;
  isSaving: boolean;
  /** Persists the changed staged attributes in one PATCH, then the staged mappings. */
  handleSave: () => Promise<void>;
  /** The first validation error of the staged changes, which disables Save & Sync. */
  saveDisabledReason?: string;
}

export interface ICrmSaveOptions<TIntegration> {
  /** Model attributes validated before a save, e.g. `unsubscribe_sync_preference.action`. */
  validatedAttributes: string[];
  /** A CRM-specific reason the staged changes can't be saved yet. */
  getSaveDisabledReason?: (integration: TIntegration) => string | undefined;
  /** Runs once the PATCH has saved. */
  onPatched?: (integration: TIntegration) => void;
}

/**
 * Page-level Save & Sync for the changes the tabs stage on the integration and the mapping draft.
 * Leaving the page discards the staged attributes; the draft is page state and drops with it.
 */
export function useCrmSave<TIntegration extends ICrmStagedIntegration>(
  integration: TIntegration | undefined,
  mappingDraft: ICrmMappingDraft,
  { getSaveDisabledReason, onPatched, validatedAttributes }: ICrmSaveOptions<TIntegration>,
): ICrmSave {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  // State lags a render behind; the ref blocks a second click that lands before the button disables.
  const isSavingRef = useRef(false);
  // A disconnected integration hides its tabs, so its staged edits can't be saved.
  const isActive = !!integration?.isActive;
  const changedAttributes = integration && isActive ? integration.changedStagedAttributes : [];
  const hasMappingChanges = isActive && mappingDraft.isDirty;
  const isDirty = changedAttributes.length > 0 || hasMappingChanges;

  // The staged attributes live on the shared model, so leaving the page has to drop them.
  useEffect(() => (): void => integration?.discardStagedChanges(), [integration]);

  function getDisabledReason(): string | undefined {
    if (!integration || !isDirty) {
      return undefined;
    }
    if (hasMappingChanges && mappingDraft.saveDisabledReason) {
      return mappingDraft.saveDisabledReason;
    }
    // `isValid` is memoized; `validate` is not, so only run it to read the message.
    if (!integration.isValid(validatedAttributes, false)) {
      return integration.validate(validatedAttributes, false).errors[0];
    }
    return getSaveDisabledReason?.(integration);
  }

  async function handleSave(): Promise<void> {
    if (!integration || isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      const isPatched =
        !changedAttributes.length ||
        (await submitModelWithErrorHandling({
          operation: integration.patch(integration.getStagedPatch()),
          onSuccess: () => onPatched?.(integration),
          t,
        }));
      if (!isPatched) {
        return;
      }
      if (hasMappingChanges) {
        try {
          await mappingDraft.save();
        } catch (error) {
          // The server may have saved some of the changes before one failed.
          toast(
            t("Couldn't save every mapping. Check the list and try again. {{- error}}", {
              error: errorMessage(error, t),
            }),
            { type: 'error' },
          );
          return;
        }
      }
      toast(t('Your changes have been saved.'), { type: 'success' });
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }

  return { handleSave, isDirty, isSaving, saveDisabledReason: getDisabledReason() };
}

/**
 * Loads the summary bar values once the integration is active, again after each save so the
 * attention count follows mapping edits, and on each poll so the last sync stays current.
 * `undefined` until the first load settles; `null` if the request failed. A reload keeps the last
 * values on screen until it settles.
 */
export function useCrmSyncSummary<TSummary>(
  integration: { id: string; isActive: boolean; getSyncSummary(): Promise<TSummary> } | undefined,
  isSaving: boolean,
  pollCount: number,
): TSummary | null | undefined {
  const [summary, setSummary] = useState<TSummary | null>();
  const isActive = !!integration?.isActive;

  // `summary` is left out on purpose: the effect sets it.
  useEffect(() => {
    if (!integration || !isActive || isSaving) {
      return;
    }
    // Ignore a response that lands after unmount, a newer load, or a save that started.
    let isCurrent = true;

    async function load(model: NonNullable<typeof integration>): Promise<void> {
      try {
        const loaded = await model.getSyncSummary();
        if (isCurrent) {
          setSummary(loaded);
        }
      } catch {
        // The bar shows each value as unavailable; the rest of the page still works.
        if (isCurrent) {
          setSummary(null);
        }
      }
    }

    void load(integration);
    return (): void => {
      isCurrent = false;
    };
  }, [integration?.id, isActive, isSaving, pollCount]);

  return summary;
}

export interface ICrmConnectionControls {
  /** Pauses the sync. Toasts on failure and resolves either way. */
  handlePause: () => Promise<void>;
  /** Resumes the sync. Toasts on failure and resolves either way. */
  handleResume: () => Promise<void>;
  /** Disconnects the integration. Toasts and rejects with the original error on failure, so a confirm modal stays open. */
  handleDisconnect: () => Promise<void>;
  /** Whether a pause, resume or disconnect request is running. */
  isChangingState: boolean;
}

export interface ICrmConnectionControlsOptions<TIntegration> {
  /** The attributes the page stages for Save & Sync, restaged after a state change. */
  stagedAttributes: readonly string[];
  /** Runs after a pause or resume succeeds. */
  onStateChanged?: (integration: TIntegration) => void;
  /** A toast for a known, expected failure, shown in place of the generic error toast. */
  getExpectedFailure?: (error: unknown) => string | undefined;
}

interface IStateChange<TIntegration> {
  request: (integration: TIntegration) => Promise<unknown>;
  failed: (error: string) => string;
  succeeded: string;
  /** A disconnect hides the tabs and stops the sync, so it skips the re-stage. */
  isDisconnect?: boolean;
}

/**
 * Pause, resume and disconnect. The model applies the server's response as saved, which would
 * drop the edits staged for Save & Sync, so they are staged again afterwards. The page blocks
 * edits and Save & Sync while `isChangingState`, so the snapshot taken before the request is
 * still current when it is staged again.
 */
export function useCrmConnectionControls<TIntegration extends ICrmStagedIntegration>(
  integration: TIntegration | undefined,
  {
    getExpectedFailure,
    onStateChanged,
    stagedAttributes,
  }: ICrmConnectionControlsOptions<TIntegration>,
): ICrmConnectionControls {
  const { t } = useTranslation();
  const [isChangingState, setIsChangingState] = useState(false);
  // State lags a render behind; the ref blocks a second click that lands before the menu disables.
  const isChangingStateRef = useRef(false);
  // The page's save hook discards staged edits when it unmounts; staging them again then would
  // bring abandoned edits back as unsaved on the next visit.
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  /** Toasts and rethrows the original error on failure. */
  async function changeState({
    failed,
    isDisconnect = false,
    request,
    succeeded,
  }: IStateChange<TIntegration>): Promise<void> {
    if (!integration || isChangingStateRef.current) {
      return;
    }
    isChangingStateRef.current = true;
    setIsChangingState(true);
    try {
      const staged: Partial<Record<string, unknown>> = integration.getStagedPatch();
      try {
        await request(integration);
      } catch (error) {
        const expected = getExpectedFailure?.(error);
        if (expected) {
          toast(expected, { type: 'warning' });
        } else {
          toast(failed(errorMessage(error, t)), { type: 'error' });
        }
        throw error;
      }
      if (!isDisconnect && isMountedRef.current) {
        // A staged attribute the response carried was applied as saved, so stage it again. One the
        // response left out (a 204) is still staged, and setting it again would record it as saved.
        stagedAttributes
          .filter((attribute) => attribute in staged && !integration.isAttributeDirty(attribute))
          .forEach((attribute) => {
            integration.set({ [attribute]: staged[attribute] });
          });
        onStateChanged?.(integration);
      }
      toast(succeeded, { type: 'success' });
    } finally {
      isChangingStateRef.current = false;
      setIsChangingState(false);
    }
  }

  async function handlePause(): Promise<void> {
    // changeState has already toasted a failure; pausing has no caller to tell.
    await changeState({
      failed: (error) =>
        t('Something went wrong while trying to pause your integration: {{- error}}', { error }),
      request: (model) => model.pauseIntegration(),
      succeeded: t('Your integration has been successfully paused.'),
    }).catch(noop);
  }

  async function handleResume(): Promise<void> {
    await changeState({
      failed: (error) =>
        t('Something went wrong while trying to resume your integration: {{- error}}', { error }),
      request: (model) => model.resumeIntegration(),
      succeeded: t('Your integration has been successfully resumed.'),
    }).catch(noop);
  }

  function handleDisconnect(): Promise<void> {
    return changeState({
      failed: (error) =>
        t('Something went wrong while trying to disconnect your integration: {{- error}}', {
          error,
        }),
      isDisconnect: true,
      request: (model) => model.disconnectIntegration(),
      succeeded: t('Your integration has been successfully disconnected.'),
    });
  }

  return { handleDisconnect, handlePause, handleResume, isChangingState };
}
